/**
 * React Error Boundary with Sentry Integration
 * 
 * Catches all React errors and reports them to Sentry
 * Shows user-friendly fallback UI
 */

import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const isDev = import.meta.env.MODE === 'development';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="bg-red-100 rounded-full p-3">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Oops! Something went wrong
        </h1>

        {/* Description */}
        <p className="text-gray-600 text-center mb-6">
          {isDev 
            ? "Don't worry, this error has been logged. See details below."
            : "We've been notified and are working on a fix. Please try again."}
        </p>

        {/* Error Details (Dev Only) */}
        {isDev && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="font-mono text-xs text-red-800 break-words">
              <strong>Error:</strong> {error.message}
            </p>
            {error.stack && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-red-700 hover:text-red-900">
                  Stack Trace
                </summary>
                <pre className="mt-2 text-xs text-red-600 overflow-x-auto whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <Button
            onClick={resetError}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
          
          <Button
            variant="outline"
            onClick={() => window.location.href = '/'}
            className="w-full"
          >
            <Home className="h-4 w-4 mr-2" />
            Go to Dashboard
          </Button>
        </div>

        {/* Support Link */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Need help?{' '}
          <a
            href="mailto:support@swiss-os-insight.com"
            className="text-red-600 hover:text-red-700 underline"
          >
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}

// Create Sentry Error Boundary
export const AppErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: (errorData) => ErrorFallback({ 
      error: errorData.error as Error, 
      resetError: errorData.resetError 
    }),
    showDialog: false, // Don't show Sentry's default dialog
  }
);
